import React, { useCallback } from 'react';
import { AppFilterButton } from 'src/components/BeaconV2Components/common/AppFilterButton/AppFilterButton';
import ComparisonPeriodDropdown from 'src/components/BeaconV2Components/common/ComparisonPeriodDropdown';
import TimePeriodDropdown, {
  getAtlasBeaconTimePeriods
} from 'src/components/BeaconV2Components/common/TimePeriodDropdown';
import {
  useTopicBuilderActions,
  StorageSource
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Store/useTopicBuilderStore';
import { useDeleteTopicGroupModal } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/hooks/useDeleteTopicGroupModal';
import { useDeleteTopicModal } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/hooks/useDeleteTopicModal';
import { Topic, TopicGroup } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/types';
import {
  isOwnedTopicOrTopicGroup,
  getTopicOrTopicGroupType
} from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils';
import { useBeaconRoutes } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { useAppSelector, useHistory } from 'src/utils/Hooks';
import { useMainEntity, useMainEntityId, useMainEntityName, useProductEntity } from 'src/utils/Hooks/reduxSelectors';
import { useTopicExportModalStore } from 'src/zustandStores/BeaconPro/Topics/Exports/TopicExportModalStore';
import { useAddTopicGroupStoreActions } from 'src/zustandStores/BeaconPro/Topics/TopicGroups/useAddTopicGroupStore';
import { useTopicGroupButtonOptionsStore } from 'src/zustandStores/BeaconPro/Topics/TopicGroups/useTopicGroupButtonOptionsStore';
import { getAppName, getQueryParamValue } from 'src/utils/app';

/**
 * Buttons for the Topics > Settings subtab header
 */
export const TopicsSubtabHeaderButtons = () => {
  const { currentSubtab } = useBeaconRoutes();
  const mainEntity = useMainEntity();
  const history = useHistory();
  const mainEntityId = useMainEntityId();
  const mainEntityName = useMainEntityName();
  const { searchParams } = useAppSelector((state) => state.app.queryParams);
  const { mode } = useTopicGroupButtonOptionsStore();
  const { openModal: openTopicGroupModal } = useDeleteTopicGroupModal();

  const { openModal: openAddTopicGroupModal, setTopicGroupState } = useAddTopicGroupStoreActions();
  const { openModal: openTopicModal } = useDeleteTopicModal();
  const { openModal: openTopicExport } = useTopicExportModalStore();
  const { storage } = useTopicBuilderActions();

  const appName = getAppName();
  const { isProductEntity } = useProductEntity();
  const currentTab = getQueryParamValue('tab');

  const handleTopicExport = useCallback(() => {
    openTopicExport();
  }, [openTopicExport]);

  const handleGroupDuplicate = () => {
    setTopicGroupState(({ setCategoryIds }) => setCategoryIds(mainEntity?.includedCategoryIds ?? []));
    setTopicGroupState(({ setSubcategoryIds }) => setSubcategoryIds(mainEntity?.includedSubCategoryIds ?? []));
    setTopicGroupState(({ setTopicIds }) => setTopicIds(mainEntity?.topicIds ?? []));
    openAddTopicGroupModal();
  };

  if (currentSubtab !== 'settings') {
    const filterElements = [
      <TimePeriodDropdown allowedTimePeriods={getAtlasBeaconTimePeriods} key="timePeriodDropdown" />,
      <ComparisonPeriodDropdown key="comparisonPeriodDropdown" />
    ];

    if (!isProductEntity || appName !== 'beacon' || currentTab !== 'traffic') {
      filterElements.push(<AppFilterButton key="appFilterButton" />);
    }
    return filterElements;
  }

  if (mainEntity.type === 'topic') {
    const entity = mainEntity as unknown as Topic;
    const isOwned = isOwnedTopicOrTopicGroup(entity.ownedByUserId);
    const topicClassification = getTopicOrTopicGroupType(mainEntity.sharedWithEntityId, mainEntity.ownedByUserId);
    const isCustom = topicClassification === 'Custom';
    const isStandard = topicClassification === 'Standard';

    if (isOwned) {
      return [
        <>
          <SlButton
            key="export"
            onClick={() => {
              handleTopicExport();
            }}
          >
            Export
          </SlButton>

          <SlButton
            key="duplicate"
            onClick={() => {
              // Save the topic to local storage to be used to hydrate the topic builder
              storage.save(entity, StorageSource.DUPLICATE);
              // Navigate to topic builder
              history.push(`/search${searchParams}&entityType=searchtermlist&action=create`);
            }}
          >
            Duplicate
          </SlButton>
          <SlButton
            buttonSx={{ backgroundColor: '#f6f7f8' }}
            key="delete"
            onClick={() => openTopicModal({ topicId: mainEntityId.toString(), topicName: mainEntityName })}
          >
            Delete
          </SlButton>
        </>
      ];
    }
    if (isCustom) {
      return [
        <>
          <SlButton
            key="export"
            onClick={() => {
              handleTopicExport();
            }}
          >
            Export
          </SlButton>
          <SlButton
            key="duplicate"
            onClick={() => {
              // Save the topic to local storage to be used to hydrate the topic builder
              storage.save(entity, StorageSource.DUPLICATE);
              // Navigate to topic builder
              history.push(`/search${searchParams}&entityType=searchtermlist&action=create`);
            }}
          >
            Duplicate
          </SlButton>
        </>
      ];
    }
    if (isStandard) {
      return [
        <>
          <SlButton
            key="export"
            onClick={() => {
              handleTopicExport();
            }}
          >
            Export
          </SlButton>
        </>
      ];
    }
    return null;
  }
  if (mainEntity.type === 'topicgroup') {
    const entity = mainEntity as unknown as TopicGroup;
    const isOwned = isOwnedTopicOrTopicGroup(entity.ownedByUserId);
    if (isOwned) {
      return [
        <>
          {mode === 'keywords' && (
            <SlButton
              key="export"
              onClick={() => {
                handleTopicExport();
              }}
            >
              Export
            </SlButton>
          )}
          <SlButton key="duplicate" onClick={() => handleGroupDuplicate()}>
            Duplicate
          </SlButton>
          <SlButton
            key="delete"
            buttonSx={{ backgroundColor: '#f6f7f8' }}
            onClick={() =>
              openTopicGroupModal({
                topicGroupId: mainEntityId.toString(),
                topicGroupName: mainEntityName
              })
            }
          >
            Delete
          </SlButton>
        </>
      ];
    }

    return [
      <>
        <SlButton
          key="export"
          onClick={() => {
            handleTopicExport();
          }}
        >
          Export
        </SlButton>
        <SlButton key="duplicate" onClick={() => handleGroupDuplicate()}>
          Duplicate
        </SlButton>
      </>
    ];
  }

  return null;
};
